import React from 'react'
import { tokens } from "../../theme";
import { Box, useTheme, Button, Typography, IconButton, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Paper, Tabs, Tab, Collapse, Accordion, AccordionSummary, AccordionDetails, Chip, CircularProgress, Tooltip, Switch } from "@mui/material";
import { alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import Loading from '../../components/Loading';
import Header from "../../components/Header";
import Axios from 'axios';
import stripHtml from '../../utils/stripHtml';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { TextField } from "@mui/material";
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Editor } from '@tinymce/tinymce-react';
import BackdropComp from '../../components/Backdrop';
import ConsultationCardReadOnly from './ConsultationCardReadOnly';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Alert from '../../components/alert/Alert';
import SemesterChipWithDialog from './SemesterChipWithDialog';
import { FileUploadOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import PrevEmailContentModal from './PrevEmailContentModal';
import StudentInfoCardModal from './StudentInfoCardModal';
import UpdatePortalModal from './UpdatePortalModal';
import { getAuthHeader } from '../../utils/authToken';
import MemoContent from '../../components/MemoContent';

const HighlightedText = ({ text, quote }) => {
    if (!quote || !text) return <>{text}</>;
    // Try exact match first
    const exactIdx = text.toLowerCase().indexOf(quote.toLowerCase());
    if (exactIdx !== -1) {
        const before = text.slice(0, exactIdx);
        const match = text.slice(exactIdx, exactIdx + quote.length);
        const after = text.slice(exactIdx + quote.length);
        return <>{before}<Box component="mark" sx={{ backgroundColor: 'rgba(255, 213, 79, 0.4)', px: 0.25, borderRadius: 0.5 }}>{match}</Box>{after}</>;
    }
    // Fuzzy: find the best matching window using keyword overlap
    const quoteWords = quote.toLowerCase().split(/\s+/).filter(w => w.length > 1);
    if (quoteWords.length === 0) return <>{text}</>;
    const sentences = text.split(/(?<=[.!?\n])\s*/);
    let bestScore = 0, bestStart = -1, bestEnd = -1;
    // Try windows of 1-3 consecutive sentences
    for (let i = 0; i < sentences.length; i++) {
        for (let j = i; j < Math.min(i + 3, sentences.length); j++) {
            const window = sentences.slice(i, j + 1).join(' ').toLowerCase();
            const score = quoteWords.filter(w => window.includes(w)).length / quoteWords.length;
            if (score > bestScore) {
                bestScore = score;
                bestStart = i;
                bestEnd = j;
            }
        }
    }
    if (bestScore < 0.4) return <>{text}</>;
    const beforeSentences = sentences.slice(0, bestStart).join(' ');
    const matchSentences = sentences.slice(bestStart, bestEnd + 1).join(' ');
    const afterSentences = sentences.slice(bestEnd + 1).join(' ');
    return <>{beforeSentences}{beforeSentences ? ' ' : ''}<Box component="mark" sx={{ backgroundColor: 'rgba(255, 213, 79, 0.4)', px: 0.25, borderRadius: 0.5 }}>{matchSentences}</Box>{afterSentences ? ' ' : ''}{afterSentences}</>;
};

const OFFICE_EMAILS = ['aone@a-oneinstitute.com', 'aoneinstituteoffice@gmail.com'];
const isSentEmail = (email) => OFFICE_EMAILS.some(e => email.from?.email?.toLowerCase() === e);

const SummaryWithRefs = ({ summary, onRefClick }) => {
    let bullets;
    try {
        const parsed = JSON.parse(summary);
        bullets = parsed.bullets;
    } catch {
        return (
            <Box sx={{ '& p': { my: 0.5, fontSize: 14, lineHeight: 1.6 }, '& ul, & ol': { pl: 2, my: 0.5 }, '& li': { fontSize: 14, lineHeight: 1.6 } }}>
                <ReactMarkdown>{summary}</ReactMarkdown>
            </Box>
        );
    }

    return (
        <Box component="ul" sx={{ pl: 2, my: 0 }}>
            {bullets.map((bullet, idx) => (
                <li key={idx} style={{ marginBottom: 8, fontSize: 14, lineHeight: 1.6 }}>
                    <Box display="inline" sx={{ '& p': { display: 'inline', m: 0 } }}>
                        <ReactMarkdown components={{ p: ({ children }) => <span>{children}</span> }}>
                            {bullet.text}
                        </ReactMarkdown>
                        {bullet.source_type && (
                            <>
                                {' '}
                                <Chip
                                    label={bullet.source_type}
                                    size="small"
                                    color={bullet.source_type === 'email' ? 'primary' : 'warning'}
                                    variant="outlined"
                                    onClick={() => onRefClick(bullet.source_type, bullet.source_index, bullet.source_quote)}
                                    sx={{ cursor: 'pointer', height: 20, fontSize: 11, verticalAlign: 'middle' }}
                                />
                            </>
                        )}
                    </Box>
                </li>
            ))}
        </Box>
    );
};

const endOfEmail = `
    <p>Best Regards,</p>
    <p>A ONE Institute</p><br /><br />

    <img src="https://aone-universe.s3.amazonaws.com/aone-images/logo(no-background).png" alt="A ONE Institute" width="200" height="39">

    <div>
        <font color="#666666"><font face="Arial, sans-serif">T.&nbsp;&nbsp;</font></font>
        <font face="Arial, sans-serif" style="color:rgb(102,102,102)">201-266-8882</font>
        <span style="color:rgb(102,102,102);font-family:바탕,serif"><font size="1">┃</font></span>
        <font face="Arial, sans-serif" style="color:rgb(102,102,102)">201-346-<wbr>5689</font>
        <span style="color:rgb(102,102,102);font-family:바탕,serif"><font size="1">┃</font></span>
        <span style="color:rgb(102,102,102);font-family:Arial,sans-serif">&nbsp;E.
        <a href="mailto:aone@a-oneinstitute.com" target="_blank">aone@a-oneinstitute.com</a></span>
    </div>

    <div>
        <font color="#666666">
        <span style="font-family:Arial,sans-serif">65 Challenger Rd Suite 201, Ridgefield Park, NJ 07660</span>
        </font>
        </div>

    <div><font color="#666666"><span style="font-family:Arial,sans-serif">
        <a href="https://www.a-oneinstitute.com" target="_blank" data-saferedirecturl="https://www.google.com/url?q=https://www.a-oneinstitute.com&amp;source=gmail&amp;ust=1693706048365000&amp;usg=AOvVaw20fPhTzBitxyJxCvPo0AsO">www.a-oneinstitute.com</a>
        </span></font></div>

    <div><font color="#666666"><span style="font-family:Arial,sans-serif"><br></span></font></div>
    `;

const options = {
    height: 800,
    menubar: false,
    plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
    ],
    toolbar: [
        ' undo redo | blocks | bold italic forecolor backcolor fontsize | fontselect |' +
        'alignleft aligncenter alignright alignjustify | lineheight |' +
        'bullist numlist outdent indent | removeformat | help'
    ],
    font_formats: 'Arial=arial,helvetica,sans-serif;' +
                 'Arial Black=arial black,avant garde;' +
                 'Courier New=courier new,courier;' +
                 'Georgia=georgia,palatino;' +
                 'Tahoma=tahoma,arial,helvetica,sans-serif;' +
                 'Times New Roman=times new roman,times;' +
                 'Verdana=verdana,geneva;' +
                 'Impact=impact,chicago;' +
                 'Comic Sans MS=comic sans ms,sans-serif;' +
                 'Trebuchet MS=trebuchet ms,geneva;',
    htmlAllowedTags:  ['.*'],
    htmlAllowedAttrs: ['.*'],
    extended_valid_elements: '*[.*]',
    content_style: "p { margin: 0; }",
};

const season = {0: 'All', 1: 'Spring', 2: 'Summer', 3: 'Fall'};

const EmailGenerator = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const minWidth90Rem = useMediaQuery('(min-width:90rem)');

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    // Communication context functions
    const fetchSummary = async (studentId) => {
        setSummaryLoading(true);
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/student-email-summaries/${studentId}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setSummaryData(res.data);
            if (res.data.is_stale || !res.data.summary) {
                await refreshSummary(studentId);
            }
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
        } finally {
            setSummaryLoading(false);
        }
    };

    const refreshSummary = async (studentId) => {
        setSummaryRefreshing(true);
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/student-email-summaries/${studentId}/generate`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setSummaryData({ ...res.data, is_stale: false });
            if (res.data.gmail_authorized === false) setGmailAuthorized(false);
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
        } finally {
            setSummaryRefreshing(false);
        }
    };

    const fetchRawEmails = async (studentId) => {
        if (emailsLoaded) return;
        setRawEmailsLoading(true);
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/student-email-summaries/${studentId}/emails`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setGmailAuthorized(res.data.authorized !== false);
            setRawEmails(res.data.emails || []);
            setEmailsLoaded(true);
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
            else setGmailAuthorized(false);
        } finally {
            setRawEmailsLoading(false);
        }
    };

    const fetchRawMemos = async (studentId) => {
        if (memosLoaded) return;
        setRawMemosLoading(true);
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/student-email-summaries/${studentId}/memos`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setRawMemos(res.data.memos || []);
            setMemosLoaded(true);
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
        } finally {
            setRawMemosLoading(false);
        }
    };

    const handleConnectGmail = async () => {
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/gmail-auth/authorize`,
                {
                    headers: { Authorization: getAuthHeader() },
                    // Preserve the full URL including query string so params like
                    // ?auto_report_log_id=... survive the OAuth round-trip.
                    params: { return_url: window.location.pathname + window.location.search },
                }
            );
            const authUrl = res.data.data?.authUrl;
            if (!authUrl) return;
            window.location.href = authUrl;
        } catch (err) {
            setErrorAlertMessage('Failed to start Gmail authorization');
            setErrorAlertOpen(true);
        }
    };

    // Check if returning from Gmail OAuth
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('gmail_auth') === 'success') {
            setGmailAuthorized(true);
            setEmailsLoaded(false);
            // Clean up URL
            params.delete('gmail_auth');
            const cleanUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // Read auto_report_log_id from URL (set by the 2-rem office notification's
    // Compose Email button). Numeric-only guard rejects garbage params.
    React.useEffect(() => {
        const fromUrl = new URLSearchParams(window.location.search).get('auto_report_log_id');
        if (fromUrl && /^\d+$/.test(fromUrl)) {
            autoReportLogIdRef.current = fromUrl;
            setAutoReportLogLinked(true);
        }
    }, []);

    // Sidecar to the email-send success handlers. If the admin arrived here via
    // a 2-rem auto-report link, transition that log row to EMAIL_SENT. Failures
    // here are silent — the email itself already went out, we don't want to
    // override the success alert with a tracking-only error.
    const markAutoReportSent = async () => {
        const logId = autoReportLogIdRef.current;
        if (!logId) return;
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${logId}/mark-sent`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            autoReportLogIdRef.current = null;
            setAutoReportLogLinked(false);
        } catch (err) {
            console.warn('[EmailGenerator] mark-sent failed:', err?.response?.data || err?.message);
        }
    };

    const handleContextTabChange = (_, newValue) => {
        setContextTab(newValue);
        if (newValue === 0 && !emailsLoaded && student?.id) {
            fetchRawEmails(student.id);
        } else if (newValue === 1 && !memosLoaded && student?.id) {
            fetchRawMemos(student.id);
        }
    };

    const handleSeparatorMouseDown = (e) => {
        e.preventDefault();
        isDraggingRef.current = true;
        const onMouseMove = (moveEvent) => {
            if (!isDraggingRef.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const offsetX = moveEvent.clientX - rect.left;
            const newEmailPercent = (offsetX / rect.width) * 100;
            const newContextPercent = 100 - newEmailPercent;
            if (newContextPercent >= 20 && newContextPercent <= 50) {
                setContextWidthPercent(newContextPercent);
            }
        };
        const onMouseUp = () => {
            isDraggingRef.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const handleRefClick = async (sourceType, sourceIndex, sourceQuote) => {
        const tabIndex = sourceType === 'email' ? 0 : 1;
        setContextTab(tabIndex);
        setContextOpen(true);
        setHighlightedRef({ type: sourceType, index: sourceIndex, quote: sourceQuote });
        if (sourceType === 'email') setExpandedEmailIdx(sourceIndex);
        if (sourceType === 'email' && !emailsLoaded && student?.id) {
            await fetchRawEmails(student.id);
        } else if (sourceType === 'memo' && !memosLoaded && student?.id) {
            await fetchRawMemos(student.id);
        }
        setTimeout(() => {
            const elId = `ref-${sourceType}-${sourceIndex}`;
            const el = document.getElementById(elId);
            const container = contextScrollRef.current;
            if (el && container) {
                const elTop = el.offsetTop - container.offsetTop;
                const centerOffset = elTop - container.clientHeight / 2 + el.clientHeight / 2;
                container.scrollTo({ top: centerOffset, behavior: 'smooth' });
            }
        }, 300);
    };

    const [prevModalOpen, setPrevModalOpen] = React.useState(false);
    const [updatePortalModalOpen, setUpdatePortalModalOpen] = React.useState(false);

    const [draftId, setDraftId] = React.useState(new URLSearchParams(window.location.search).get('draft'));

    // Auto-report tracking: when the admin arrives here via the "Compose Email"
    // button in the 2-remaining office notification, the URL carries
    // ?auto_report_log_id=N. On successful send we POST to /auto-reports/:id/mark-sent
    // so the log row can transition OFFICE_NOTIFIED → EMAIL_SENT. The URL is the
    // sole source of truth — handleConnectGmail passes the full URL (incl. query
    // string) as OAuth return_url so the param survives the round-trip.
    const autoReportLogIdRef = React.useRef(null);
    const [autoReportLogLinked, setAutoReportLogLinked] = React.useState(false);

    let first_render = true;

    // Auto-save to localStorage
    const [autoSaveStatus, setAutoSaveStatus] = React.useState('idle'); // 'idle' | 'saved' | 'error'
    const autoSaveTimerRef = React.useRef(null);
    const hasUserEditedRef = React.useRef(false);
    const sessionIdRef = React.useRef(`${window.location.pathname.split('/')[3]}_${Date.now()}`);
    const [restoreDialogOpen, setRestoreDialogOpen] = React.useState(false);
    const restoreDataRef = React.useRef(null);

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    const student_uuid = window.location.pathname.split('/')[3];

    const [loading, setLoading] = React.useState(true);
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const [enableFollowup, setEnableFollowup] = React.useState(true);
    const [gmailFailedDialogOpen, setGmailFailedDialogOpen] = React.useState(false);
    const [gmailFailedPayload, setGmailFailedPayload] = React.useState(null);
    const [followupRetryOpen, setFollowupRetryOpen] = React.useState(false);
    const [followupRetryPayload, setFollowupRetryPayload] = React.useState(null);
    const [followupRetryError, setFollowupRetryError] = React.useState("");
    const [followupRetryLoading, setFollowupRetryLoading] = React.useState(false);
    const [gmailAuthRequiredOpen, setGmailAuthRequiredOpen] = React.useState(false);
    const [gmailAuthChecking, setGmailAuthChecking] = React.useState(false);
    const [gmailAuthError, setGmailAuthError] = React.useState("");
    const [gmailAuthorizeStarted, setGmailAuthorizeStarted] = React.useState(false);
    const pendingSendRef = React.useRef(null);

    const [consultationCardToggle, setConsultationCardToggle] = React.useState(false);
  
    const [semesters, setSemesters] = React.useState([]);
    const [selectedSemester, setSelectedSemester] = React.useState(0);

    const [categoryWithTemplates, setCategoryWithTemplates] = React.useState([]);
    const [courseTitleCheckedMap, setCourseTitleCheckedMap] = React.useState({});
    const [selectedCourses, setSelectedCourses] = React.useState([]);

    const [student, setStudent] = React.useState({});

    // Communication context panel
    const [contextOpen, setContextOpen] = React.useState(true);
    const [contextTab, setContextTab] = React.useState(0);
    const [summaryData, setSummaryData] = React.useState(null);
    const [summaryLoading, setSummaryLoading] = React.useState(false);
    const [summaryRefreshing, setSummaryRefreshing] = React.useState(false);
    const [rawEmails, setRawEmails] = React.useState([]);
    const [rawEmailsLoading, setRawEmailsLoading] = React.useState(false);
    const [emailsLoaded, setEmailsLoaded] = React.useState(false);
    const [rawMemos, setRawMemos] = React.useState([]);
    const [rawMemosLoading, setRawMemosLoading] = React.useState(false);
    const [memosLoaded, setMemosLoaded] = React.useState(false);
    const [gmailAuthorized, setGmailAuthorized] = React.useState(null);
    const [highlightedRef, setHighlightedRef] = React.useState(null);
    const [expandedEmailIdx, setExpandedEmailIdx] = React.useState(null);
    const [contextWidthPercent, setContextWidthPercent] = React.useState(30);
    const isDraggingRef = React.useRef(false);
    const containerRef = React.useRef(null);
    const contextScrollRef = React.useRef(null);
    const isWideScreen = useMediaQuery('(min-width:1200px)');

    const [senderEmail, setSenderEmail] = React.useState('aone@a-oneinstitute.com');
    const [recipientEmail, setRecipientEmail] = React.useState('');

    const [author, setAuthor] = React.useState('');
    const [title, setTitle] = React.useState('');
    const [date, setDate] = React.useState(dayjs());

    const [emailContent, setEmailContent] = React.useState(`
        <div>
            안녕하세요.
            <br />
            A ONE 실장 Hailey Hong 입니다.
            <br />
            귀한 시간내어 연락주셔서 감사합니다.
            <br />
            <br />
            보다 나은 상담과 안내를 드리기 위해, 학부모님께서 어떤 경로를 통해 저희 A ONE Institute를 알게 되셨는지 간단히 여쭤보고자 합니다.
            <br />
            아래에 짧은 링크를 첨부드렸으며, 가능하실 때 편하신 마음으로 작성해 주시면 큰 도움이 될 것 같습니다.
            <br />
            <br />
            협조에 감사드리며, 문의 하신 내용 안내드립니다.
            <br />
            <br />
            링크: <a href="https://docs.google.com/forms/d/e/1FAIpQLSekAaUdkaeZI_NB_a_AEP1HK1m7rTiPKiqtKkVdW8W6YLCrIQ/viewform">How did you hear about A ONE Institute?</a>
            <br />
            <br />
            그리고 저희는 학부모님들께서 학생의 수업 진행 상황을 보다 편하게 확인하실 수 있도록 Parent Portal을 함께 운영하고 있습니다.
            <br />
            <br />
            Parent Portal을 통해 학부모님께서는 학생의 수업 피드백, 숙제 진행 여부, 시험 결과, 결석시 동영상 수업 그리고 학기말 리포트 등을 실시간으로 확인하실 수 있습니다.
            수업이 어떻게 진행되고 있는지, 학생이 어떤 부분을 잘 따라오고 있고 어떤 부분을 더 보완해야 하는지를 부모님께서도 직접 확인하실 수 있도록 하기 위한 시스템입니다.
            <br />
            <br />
            저희가 Parent Portal을 운영하는 이유는 수업 과정을 보다 투명하고 정확하게 공유하기 위해서입니다.
            이를 통해 학부모님께서도 간접적으로 저희 수업의 흐름을 확인하시고, 학생의 현재 학습 상황을 보다 명확하게 파악하실 수 있습니다.
            <br />
            <br />
            Parent Portal에 대한 구체적인 사용 방법과 안내는 수업이 확정된 이후에 다시 자세하게 설명드리고 있습니다.</p>
        </div>
    `);

    const [draft, setDraft] = React.useState(null);

    // const [eachCategoryTrueNum, setEachCategoryTrueNum] = React.useState({
    //     'English': 0,
    //     'Math': 0,
    //     'Science': 0,
    //     'Social': 0,
    //     'Competition': 0,
    //     'Test Prep': 0,
    // });

    // for file upload
    const [files, setFiles] = React.useState([]);
    const handleFileRemove = (event, index) => {
        event.preventDefault();
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };
    const handleUpload = (event) => {
        setFiles([...files, ...Array.from(event.target.files)]);

        const formData = new FormData();
        for (let i = 0; i < event.target.files.length; i++) {
            formData.append("file", event.target.files[i]);
        }
    };
    
    const handleChange = (event, category, course) => {
        // setEachCategoryTrueNum(
        //     event.target.checked ? eachCategoryTrueNum[course.course_title] + 1 : eachCategoryTrueNum[course.course_title] - 1
        // );

        if (course.course_id === undefined) return;
        if (course.course_id !== null) {
            setCourseTitleCheckedMap({
                ...courseTitleCheckedMap,
                [course.course_title + "|" + course.course_id]: event.target.checked,
            });

            if (event.target.checked && (course.email_template !== undefined && course.email_template !== null && course.email_template !== "")) {
                setEmailContent(
                    emailContent + `\n\n${course.email_template}\n\n`
                )
                let is_already_selected = false;
                for (let i = 0; i < selectedCourses.length; i++) {
                    if (selectedCourses[i] === course.course_id) {
                        is_already_selected = true;
                        break;
                    }
                }
                if (!is_already_selected) {
                    setSelectedCourses([...selectedCourses, course.course_id]);
                }
            } else {
                setEmailContent(
                    emailContent.replace(
                        course.email_template, ''
                    )
                )
                setSelectedCourses(selectedCourses.filter((selectedCourse) => selectedCourse !== course.course_id));
            }
        } else {
            // dictionary with course_id is null, meaning that it is for combined courses
            const course_ids = course.course_ids;
            let checkMap = {
                [course.course_title + "|" + course.course_id]: event.target.checked,
            };

            // check all the courses in the combined courses
            for (let i = 0; i < course.course_titles.length; i++) {
                checkMap[course.course_titles[i] + "|" + course.course_ids[i]] = event.target.checked;
            }

            setCourseTitleCheckedMap({
                ...courseTitleCheckedMap,
                ...checkMap,
            });

            if (event.target.checked) {
                if (course.email_template !== undefined && course.email_template !== null && course.email_template !== "") {
                    setEmailContent(
                        emailContent + `\n\n${course.email_template}\n\n`
                    )
                }
                let not_in_selected_courses = course_ids.filter((course_id) => !selectedCourses.includes(course_id));
                setSelectedCourses([...selectedCourses, ...not_in_selected_courses]);
            } else {
                setEmailContent(
                    emailContent.replace(
                        course.email_template, ''
                    )
                )
                setSelectedCourses(selectedCourses.filter((selectedCourse) => !course_ids.includes(selectedCourse)));
            }
        }
    };

    async function handleSaveDraft() {
        setBackdropOpen(true);
        const courseTitleAndId = Object.keys(courseTitleCheckedMap).filter((courseTitleAndId) => courseTitleCheckedMap[courseTitleAndId] === true);
        const course_titles = courseTitleAndId.map((courseTitleAndId) => {
            // remove course_id from courseTitleAndId
            if (!courseTitleAndId) return null;
            return courseTitleAndId.split("|")?.slice(0, courseTitleAndId.split("|").length - 1).join("|");
        });
        const selectedCourses = courseTitleAndId.map((courseTitleAndId) => {
            if (!courseTitleAndId) return null;
            return courseTitleAndId.split("|")[courseTitleAndId.split("|").length - 1];
        });

        if (senderEmail === null || senderEmail.trim() === '') {
            setErrorAlertMessage("Sender Email is required!");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (recipientEmail === null || recipientEmail.trim() === '') {
            setErrorAlertMessage("Recipient Email is required! Please check the student's parent email.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (title === null || title.trim() === '') {
            setErrorAlertMessage("Subject is required!");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (author === null || author.trim() === '') {
            setErrorAlertMessage("Author is required!");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (draftId === null) {
            // save draft
            const res = await Axios.post(`${process.env.REACT_APP_URL}/api/v1/info-email-drafts`, {
                student_uuid,
                course_ids: selectedCourses,
                course_titles: course_titles,
                email_author: author,
                sender_email: senderEmail,
                recipient_email: recipientEmail.trim(),
                subject: title.trim(),
                content: emailContent,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });

            if (res.status !== 200) {
                if (res.status === 401) {
                    handleLogout();
                    return;
                }

                setErrorAlertMessage(res.data.message || res.data.error);
                setErrorAlertOpen(true);
                return;
            }

            // status 201 here

            const draft_id = res.data.draft_id;

            if (files.length > 0) {
                const result = await saveAttachedFiles(draft_id);

                if (result.status !== 200) {
                    setErrorAlertMessage("Error occurs while saving attached files!");
                    setErrorAlertOpen(true);
                    return;
                }
            }
            
            setSuccessAlertMessage("Draft saved successfully");
            setSuccessAlertOpen(true);


            setTimeout(() => {
                setDraftId(draft_id);
            }, 300);

        } else {
            // update draft
            const res = await Axios.put(`${process.env.REACT_APP_URL}/api/v1/info-email-drafts/${draftId}`, {
                student_uuid,
                course_ids: selectedCourses,
                course_titles: course_titles,
                email_author: author,
                sender_email: senderEmail,
                recipient_email: recipientEmail.trim(),
                subject: title.trim(),
                content: emailContent,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            
            });


            if (res.status !== 200) {
                if (res.status === 401) {
                    handleLogout();
                    return;
                }
                
                setErrorAlertMessage(res.data.message ? res.data.message : res.data.error ? res.data.error : "Something went wrong!");
                setErrorAlertOpen(true);
                return;
            }

            // status 200 here

            if (files.length > 0) {
                const result = await saveAttachedFiles(draftId);
                if (result.status !== 200) {
                    setErrorAlertMessage("Error occurs while saving attached files!");
                    setErrorAlertOpen(true);
                    return;
                }
            }

            setSuccessAlertMessage("Draft updated successfully");
            setSuccessAlertOpen(true);

        }
        setBackdropOpen(false);
    }

    function saveAttachedFiles(draft_id) {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append("file", files[i]);
        }

        return Axios.post(`${process.env.REACT_APP_URL}/api/v1/info-email-drafts/${draft_id}/draft-attached-files`, formData, {
            headers: {
                Authorization: getAuthHeader(),
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    
    async function handleSendEmail() {
        setBackdropOpen(true);

        if (senderEmail === null || senderEmail.trim() === '') {
            setErrorAlertMessage("Sender Email is required!");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (recipientEmail === null || recipientEmail.trim() === '') {
            setErrorAlertMessage("Recipient Email is required! Please check the student's parent email.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (title === null || title.trim() === '') {
            setErrorAlertMessage("Subject is required!");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        if (author === null || author.trim() === '') {
            setErrorAlertMessage("Author is required!");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        // save it to local storage
        const prevEmailContents = JSON.parse(localStorage.getItem('prevEmailContents')) ?? [];
        if (prevEmailContents.length >= 5) {
            prevEmailContents.shift();
        }
        prevEmailContents.push(emailContent);
        localStorage.setItem('prevEmailContents', JSON.stringify(prevEmailContents));

        const email_content = pToDiv(emailContent) + endOfEmail;

        // Snapshot every field needed for sending at click time so that the
        // Retry / SMTP-fallback paths from the auth-required modal use
        // consistent values regardless of subsequent edits.
        const snapshot = {
            email_content,
            senderEmail,
            recipientEmail: recipientEmail.trim(),
            author,
            date: date.hour(12).toISOString(),
            subject: title.trim(),
            draft_id: draftId === null ? null : parseInt(draftId),
            files: [...files],
            selectedCourses: [...selectedCourses],
            enable_followup: enableFollowup,
        };

        const performSend = async () => {
            setBackdropOpen(true);

            const handleSendSuccess = async (response) => {
                if (response.data.gmail_failed) {
                    // Gmail API failed — show dialog to let admin choose SMTP fallback
                    setGmailFailedPayload({
                        email_content: snapshot.email_content,
                        senderEmail: snapshot.senderEmail,
                        recipientEmail: snapshot.recipientEmail,
                        author: snapshot.author,
                        date: snapshot.date,
                        subject: snapshot.subject,
                        draft_id: snapshot.draft_id,
                        hasAttachments: snapshot.files.length > 0,
                        files: snapshot.files,
                        selectedCourses: snapshot.selectedCourses,
                    });
                    setGmailFailedDialogOpen(true);
                    setBackdropOpen(false);
                    return;
                }

                // Fire-and-forget: if this send came from a 2-rem auto-report
                // link, transition the log to EMAIL_SENT in the background.
                markAutoReportSent();

                // If the followup-tracking insert failed server-side, open a modal
                // so the admin can retry it manually. The email itself was sent OK.
                if (response.data.followup_failed && response.data.followup_thread_id) {
                    setFollowupRetryPayload({
                        gmail_thread_id: response.data.followup_thread_id,
                        recipient_email: snapshot.recipientEmail,
                        student_id: response.data.student_id,
                        subject: snapshot.subject,
                        author: snapshot.author,
                    });
                    setFollowupRetryError("");
                    setFollowupRetryOpen(true);
                }

                if (snapshot.selectedCourses.length !== 0) {
                    await Axios.post(`${process.env.REACT_APP_URL}/api/v1/students/${student_uuid}/potential-courses`, {
                        course_ids: snapshot.selectedCourses,
                        date: snapshot.date,
                        author: snapshot.author,
                    }, {
                        headers: { Authorization: getAuthHeader() },
                    }).then(() => {
                        const trackingMsg = response.data.followup_tracking ? " Follow-up tracking is active (3 days)." : "";
                        setSuccessAlertMessage("Email sent successfully and student added to the selected potential courses." + trackingMsg);
                        setSuccessAlertOpen(true);
                    }).catch((error) => {
                        if (error.response?.status === 401) { handleLogout(); return; }
                        const detail = error.response?.data?.message || error.response?.data?.error || "Something went wrong";
                        setErrorAlertMessage(`Email sent, but adding the student to the selected potential courses failed: ${detail}`);
                        setErrorAlertOpen(true);
                    });
                } else {
                    const trackingMsg = response.data.followup_tracking ? " Follow-up tracking is active (3 days)." : "";
                    setSuccessAlertMessage("Email sent successfully." + trackingMsg);
                    setSuccessAlertOpen(true);
                }
            };

            const handleSendError = (error) => {
                if (error.response?.status === 401) { handleLogout(); return; }
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong");
                setErrorAlertOpen(true);
            };

            if (snapshot.files.length > 0) {
                const formData = new FormData();
                for (let i = 0; i < snapshot.files.length; i++) {
                    formData.append("file", snapshot.files[i]);
                }

                formData.append("senderEmail", snapshot.senderEmail);
                formData.append("recipientEmail", snapshot.recipientEmail);
                formData.append("author", snapshot.author);
                formData.append("date", snapshot.date);
                formData.append("subject", snapshot.subject);
                formData.append("content", snapshot.email_content);
                formData.append("draft_id", snapshot.draft_id);
                formData.append("enable_followup", snapshot.enable_followup.toString());

                await Axios.post(`${process.env.REACT_APP_URL}/api/v1/mails/students/${student_uuid}/email-generator/with-attachments`, formData, {
                    headers: {
                        Authorization: getAuthHeader(),
                        'Content-Type': 'multipart/form-data',
                    },
                }).then(handleSendSuccess).catch(handleSendError);
            } else {
                await Axios.post(`${process.env.REACT_APP_URL}/api/v1/mails/students/${student_uuid}/email-generator`, {
                    senderEmail: snapshot.senderEmail,
                    recipientEmail: snapshot.recipientEmail,
                    author: snapshot.author,
                    date: snapshot.date,
                    subject: snapshot.subject,
                    content: snapshot.email_content,
                    draft_id: snapshot.draft_id,
                    enable_followup: snapshot.enable_followup,
                }, {
                    headers: { Authorization: getAuthHeader() },
                }).then(handleSendSuccess).catch(handleSendError);
            }

            setBackdropOpen(false);
        };

        // Pre-flight: check Gmail auth status. If unauthorized, open the
        // auth-required modal and stash performSend + the snapshot in a ref
        // so the modal's retry / SMTP buttons can re-enter the pipeline
        // without re-running validation. If the status endpoint itself fails,
        // log and proceed — the existing post-send fallback still catches
        // real Gmail failures.
        try {
            const statusRes = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/gmail-auth/status`,
                { headers: { Authorization: getAuthHeader() } }
            );
            if (statusRes.data?.data?.authorized === false) {
                pendingSendRef.current = { performSend, snapshot };
                setGmailAuthError("");
                setGmailAuthorizeStarted(false);
                setGmailAuthRequiredOpen(true);
                setBackdropOpen(false);
                return;
            }
        } catch (err) {
            if (err.response?.status === 401) { handleLogout(); return; }
            console.warn('Gmail auth pre-flight failed; proceeding with send', err);
        }

        await performSend();
    };

    // Authorize Gmail: open the OAuth consent screen in a new tab.
    async function handleAuthorizeGmail() {
        setGmailAuthChecking(true);
        setGmailAuthError("");
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/gmail-auth/authorize`,
                { headers: { Authorization: getAuthHeader() } }
            );
            const authUrl = res.data?.data?.authUrl;
            if (!authUrl) throw new Error('No auth URL returned');
            window.open(authUrl, '_blank', 'noopener');
            setGmailAuthorizeStarted(true);
        } catch (err) {
            if (err.response?.status === 401) { handleLogout(); return; }
            setGmailAuthError('Failed to start Gmail authorization. Please try again.');
        } finally {
            setGmailAuthChecking(false);
        }
    }

    // After authorizing in another tab, re-check status and resume the send.
    async function handleAuthRecheckAndSend() {
        setGmailAuthChecking(true);
        setGmailAuthError("");
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/gmail-auth/status`,
                { headers: { Authorization: getAuthHeader() } }
            );
            if (res.data?.data?.authorized === false) {
                setGmailAuthError("Gmail still appears to be unauthorized. Please complete the authorization in the other tab and try again.");
                return;
            }
            const pending = pendingSendRef.current;
            setGmailAuthRequiredOpen(false);
            setGmailAuthorizeStarted(false);
            pendingSendRef.current = null;
            if (pending && typeof pending.performSend === 'function') {
                await pending.performSend();
            }
        } catch (err) {
            if (err.response?.status === 401) { handleLogout(); return; }
            setGmailAuthError('Failed to re-check Gmail authorization. Please try again.');
        } finally {
            setGmailAuthChecking(false);
        }
    }

    // From the auth-required modal, route through the existing SMTP fallback
    // by building a gmailFailedPayload and invoking handleSmtpFallback.
    function handleAuthRequiredSmtpFallback() {
        const pending = pendingSendRef.current;
        if (!pending) {
            setGmailAuthRequiredOpen(false);
            return;
        }
        const s = pending.snapshot;
        const smtpPayload = {
            email_content: s.email_content,
            senderEmail: s.senderEmail,
            recipientEmail: s.recipientEmail,
            author: s.author,
            date: s.date,
            subject: s.subject,
            draft_id: s.draft_id,
            hasAttachments: s.files.length > 0,
            files: s.files,
            selectedCourses: s.selectedCourses,
        };
        setGmailAuthRequiredOpen(false);
        setGmailAuthorizeStarted(false);
        pendingSendRef.current = null;
        handleSmtpFallback(smtpPayload);
    }

    function handleAuthRequiredCancel() {
        setGmailAuthRequiredOpen(false);
        setGmailAuthorizeStarted(false);
        setGmailAuthError("");
        pendingSendRef.current = null;
    }

    // SMTP fallback handler (called from Gmail failure dialog)
    // Uses captured payload to avoid stale component state
    async function handleSmtpFallback(payloadOverride) {
        setGmailFailedDialogOpen(false);
        setBackdropOpen(true);

        const payload = payloadOverride || gmailFailedPayload;

        const handleFallbackSuccess = async (response) => {
            // Fire-and-forget: if this send came from a 2-rem auto-report link,
            // transition the log to EMAIL_SENT in the background.
            markAutoReportSent();

            if (payload.selectedCourses.length !== 0) {
                await Axios.post(`${process.env.REACT_APP_URL}/api/v1/students/${student_uuid}/potential-courses`, {
                    course_ids: payload.selectedCourses,
                    date: payload.date,
                    author: payload.author,
                }, {
                    headers: { Authorization: getAuthHeader() },
                }).then(() => {
                    setSuccessAlertMessage("Email sent via SMTP. Auto follow-up is not available.");
                    setSuccessAlertOpen(true);
                }).catch((error) => {
                    if (error.response?.status === 401) { handleLogout(); return; }
                    const detail = error.response?.data?.message || error.response?.data?.error || "Something went wrong";
                    setErrorAlertMessage(`Email sent via SMTP, but adding the student to the selected potential courses failed: ${detail}`);
                    setErrorAlertOpen(true);
                });
            } else {
                setSuccessAlertMessage("Email sent via SMTP. Auto follow-up is not available.");
                setSuccessAlertOpen(true);
            }
        };

        const handleFallbackError = (error) => {
            if (error.response?.status === 401) { handleLogout(); return; }
            setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong");
            setErrorAlertOpen(true);
        };

        if (payload.hasAttachments) {
            const formData = new FormData();
            for (let i = 0; i < payload.files.length; i++) {
                formData.append("file", payload.files[i]);
            }
            formData.append("senderEmail", payload.senderEmail);
            formData.append("recipientEmail", payload.recipientEmail);
            formData.append("author", payload.author);
            formData.append("date", payload.date);
            formData.append("subject", payload.subject);
            formData.append("content", payload.email_content);
            formData.append("draft_id", payload.draft_id);

            await Axios.post(`${process.env.REACT_APP_URL}/api/v1/mails/students/${student_uuid}/email-generator/smtp/with-attachments`, formData, {
                headers: {
                    Authorization: getAuthHeader(),
                    'Content-Type': 'multipart/form-data',
                },
            }).then(handleFallbackSuccess).catch(handleFallbackError);
        } else {
            await Axios.post(`${process.env.REACT_APP_URL}/api/v1/mails/students/${student_uuid}/email-generator/smtp`, {
                senderEmail: payload.senderEmail,
                recipientEmail: payload.recipientEmail,
                author: payload.author,
                date: payload.date,
                subject: payload.subject,
                content: payload.email_content,
                draft_id: payload.draft_id,
            }, {
                headers: { Authorization: getAuthHeader() },
            }).then(handleFallbackSuccess).catch(handleFallbackError);
        }

        setBackdropOpen(false);
        setGmailFailedPayload(null);
    };

    // Retry creating an email_followups row when the initial insert failed
    // server-side after the email was already sent via Gmail.
    async function handleFollowupRetry() {
        if (!followupRetryPayload) return;
        setFollowupRetryLoading(true);
        setFollowupRetryError("");
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/email-followups/track`,
                followupRetryPayload,
                { headers: { Authorization: getAuthHeader() } }
            );
            setFollowupRetryOpen(false);
            setFollowupRetryPayload(null);
            setSuccessAlertMessage("Follow-up tracking enabled.");
            setSuccessAlertOpen(true);
        } catch (error) {
            if (error.response?.status === 401) { handleLogout(); return; }
            setFollowupRetryError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Failed to enable follow-up tracking. Please try again."
            );
        } finally {
            setFollowupRetryLoading(false);
        }
    }

    React.useEffect(() => {
        localStorage.setItem('selectedMenu', '상담 진행 상황');

        function base64ToBlob(base64, contentType = '') {
            const byteCharacters = atob(base64);
            const byteArrays = [];
          
            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);
                const byteNumbers = new Array(slice.length);
            
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
            
                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }
          
            return new Blob(byteArrays, { type: contentType });
        }          

        function createAttachedFiles(draft_attached_files) {
            let files = [];
            for (let i = 0; i < draft_attached_files.length; i++) {
                try {
                    // Convert Base64 to Blob
                    const blob = base64ToBlob(draft_attached_files[i].base64_content, draft_attached_files[i].file_type);

                    // Create a File object from the Blob
                    const newFile = new File([blob], draft_attached_files[i].file_name, { type: draft_attached_files[i].file_type });

                    files.push(newFile);
                } catch (error) {
                    console.error(error);
                }
            }

            setFiles(files);
        }

        async function fetchDraft() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/info-email-drafts/${draftId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                setSenderEmail(response.data.sender_email);
                setAuthor(response.data.email_author);
                setTitle(response.data.subject);
                setDraft(response.data);
                setEmailContent(response.data.content);

                if (response.data.draft_attached_files && response.data.draft_attached_files.length > 0) {
                    createAttachedFiles(response.data.draft_attached_files);
                }

            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong!");
                setErrorAlertOpen(true);
            });
        }

        async function fetchStudent() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${student_uuid}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                setStudent(response.data);
                setRecipientEmail(response.data.parent_email);
                if (response.data.id) {
                    fetchSummary(response.data.id);
                    fetchRawEmails(response.data.id);
                }
            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong!");
                setErrorAlertOpen(true);
            });
        }

        async function fetchSemesters() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/semesters`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                setSemesters(response.data);
                const curr_semester = getCurrSemester(response.data);
                setSelectedSemester(curr_semester);
            }).catch((error) => {
                if (error.response?.status === 404) {
                    setSemesters([]);
                    return;
                } else if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong!");
                setErrorAlertOpen(true);
            });
        }

        if (draftId !== null) {
            fetchDraft();
        }

        async function initData() {
            await fetchStudent();
            await fetchSemesters();

            // Check prevEmailContents for a matching student_uuid entry
            if (draftId === null) {
                try {
                    const prevEmailContents = JSON.parse(localStorage.getItem('prevEmailContents')) ?? [];
                    const matches = prevEmailContents
                        .filter(e => e.student_uuid === student_uuid && e.savedAt)
                        .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
                    const match = matches[0];
                    if (match) {
                        const hoursSinceSave = (Date.now() - new Date(match.savedAt).getTime()) / (1000 * 60 * 60);
                        if (hoursSinceSave < 24) {
                            restoreDataRef.current = match;
                            setRestoreDialogOpen(true);
                            setLoading(false);
                            return; // Don't enable auto-save until dialog is dismissed
                        }
                    }
                } catch (error) {
                    console.error('Failed to check auto-save:', error);
                }
            }

            setLoading(false);
            hasUserEditedRef.current = true;
        }

        initData();
    }, [draftId]);

    React.useEffect(() => {
        async function fetchCategoryWithEmailTemplates() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/potential-courses/semesters/${selectedSemester}/category-with-templates`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                let courseTitleCheckedMap = response.data.courseTitleCheckedMap;
                if (first_render && draft !== null) {
                    first_render = false;

                    for (let i = 0; i < draft.course_titles.length; i++) {
                        if (draft.course_titles[i].includes("Combined")) {
                            courseTitleCheckedMap[draft.course_titles[i] + "|" + null] = true;
                            continue;
                        }
                        courseTitleCheckedMap[draft.course_titles[i] + "|" + draft.course_ids[i]] = true;
                    }

                    setSelectedCourses(draft.course_ids || []);
                }

                setCategoryWithTemplates(response.data.categoryWithTemplates);
                setCourseTitleCheckedMap(courseTitleCheckedMap);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                } else if (error.response?.status === 404) {
                    setCategoryWithTemplates([]);
                    setCourseTitleCheckedMap({});
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong!");
                setErrorAlertOpen(true);
            });
            setLoading(false);
        }

        if (selectedSemester === 0) return;

        setLoading(true);
        fetchCategoryWithEmailTemplates();
    }, [selectedSemester, draft]);

    // Auto-save to localStorage (debounced 3s)
    React.useEffect(() => {
        if (!hasUserEditedRef.current) return;

        setAutoSaveStatus('idle');

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            try {
                const checkedKeys = Object.keys(courseTitleCheckedMap).filter(k => courseTitleCheckedMap[k] === true);
                const course_titles = checkedKeys.map(k => k.split("|").slice(0, -1).join("|"));
                const course_ids = checkedKeys.map(k => k.split("|").pop());
                const entry = {
                    id: sessionIdRef.current,
                    student_uuid,
                    content: emailContent,
                    title,
                    senderEmail,
                    recipientEmail,
                    author,
                    course_ids,
                    course_titles,
                    date: date ? date.toISOString() : null,
                    savedAt: new Date().toISOString(),
                };
                const prevEmailContents = JSON.parse(localStorage.getItem('prevEmailContents')) ?? [];
                const existingIndex = prevEmailContents.findIndex(e => e.id === sessionIdRef.current);
                if (existingIndex !== -1) {
                    prevEmailContents[existingIndex] = entry;
                } else {
                    if (prevEmailContents.length >= 5) {
                        prevEmailContents.shift();
                    }
                    prevEmailContents.push(entry);
                }
                localStorage.setItem('prevEmailContents', JSON.stringify(prevEmailContents));
                setAutoSaveStatus('saved');
            } catch (error) {
                console.error('Auto-save failed:', error);
                setAutoSaveStatus('error');
            }
        }, 3000);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [emailContent, title, senderEmail, recipientEmail, author, date, student_uuid, courseTitleCheckedMap]);

    // Save to localStorage immediately on tab close
    React.useEffect(() => {
        const handleBeforeUnload = () => {
            if (hasUserEditedRef.current) {
                try {
                    const checkedKeys = Object.keys(courseTitleCheckedMap).filter(k => courseTitleCheckedMap[k] === true);
                    const course_titles = checkedKeys.map(k => k.split("|").slice(0, -1).join("|"));
                    const course_ids = checkedKeys.map(k => k.split("|").pop());
                    const entry = {
                        id: sessionIdRef.current,
                        student_uuid,
                        content: emailContent,
                        title,
                        senderEmail,
                        recipientEmail,
                        author,
                        course_ids,
                        course_titles,
                        date: date ? date.toISOString() : null,
                        savedAt: new Date().toISOString(),
                    };
                    const prevEmailContents = JSON.parse(localStorage.getItem('prevEmailContents')) ?? [];
                    const existingIndex = prevEmailContents.findIndex(e => e.id === sessionIdRef.current);
                    if (existingIndex !== -1) {
                        prevEmailContents[existingIndex] = entry;
                    } else {
                        if (prevEmailContents.length >= 5) {
                            prevEmailContents.shift();
                        }
                        prevEmailContents.push(entry);
                    }
                    localStorage.setItem('prevEmailContents', JSON.stringify(prevEmailContents));
                } catch (error) {
                    console.error('Auto-save on unload failed:', error);
                }
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [emailContent, title, senderEmail, recipientEmail, author, date, student_uuid, courseTitleCheckedMap]);

    const handleChipClick = (semester_id) => {
        setSelectedSemester(semester_id);
    };

    function pToDiv(emailContent) {
        return emailContent.replace(/<p>/g, '<div>').replace(/<\/p>/g, '</div>').replace(/<p /g, '<div ');
    }

    const handleRestore = () => {
        const data = restoreDataRef.current;
        if (data) {
            if (data.content) setEmailContent(data.content);
            if (data.title) setTitle(data.title);
            if (data.senderEmail) setSenderEmail(data.senderEmail);
            if (data.recipientEmail) setRecipientEmail(data.recipientEmail);
            if (data.author) setAuthor(data.author);
            if (data.date) setDate(dayjs(data.date));
            // Set draft so fetchCategoryWithEmailTemplates restores course checkboxes
            if (data.course_ids && data.course_titles) {
                setDraft({ course_ids: data.course_ids, course_titles: data.course_titles });
            }
            // Reuse the same session ID so auto-save updates this entry
            sessionIdRef.current = data.id;
        }
        setRestoreDialogOpen(false);
        // Enable auto-save after restore is done (with delay to let state settle)
        setTimeout(() => { hasUserEditedRef.current = true; }, 1000);
    };

    const handleSkipRestore = () => {
        setRestoreDialogOpen(false);
        hasUserEditedRef.current = true;
    };

    function getCurrSemester(semesters) {
        if (semesters.length === 0) return 0;

        let curr_date = dayjs();
        const Jan29 = dayjs().month(0).date(29);
        if (curr_date.isBefore(Jan29)) {
            curr_date = Jan29;
        }

        for (let i = 0; i < semesters.length; i++) {
            const start_date = dayjs(semesters[i].start);
            const end_date = dayjs(semesters[i].end);
            if (curr_date.isAfter(start_date) && curr_date.isBefore(end_date)) {
                return semesters[i].id;
            }
        }
        return semesters[0].id;
    }

    return (
        <Box 
            m="20px" 
            marginX={3} 
            marginY={3} 
            sx={{
                minHeight: "88%",
                position: 'relative',
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title="Email Generator" subtitle="A ONE Institute" />
                {process.env.NODE_ENV === 'development' && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/admin/dev/email-followups')}
                    >
                        Followup Dev Panel
                    </Button>
                )}
            </Box>
            { loading ? 
                <Loading />
            :
                <Box ref={containerRef} sx={{ display: 'flex', flexDirection: isWideScreen ? 'row' : 'column', gap: 0 }}>
                {/* Email Form (left side) */}
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="left"
                    gap={2}
                    p={0}
                    sx={{ width: isWideScreen && student?.id ? `${100 - contextWidthPercent}%` : '100%' }}
                    minHeight={70}
                    mb={2}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            border: `1px solid ${colors.grey[700]}`,
                            padding: 2,
                        }}
                    >
                        <Typography
                            color={colors.redAccent[500]}
                            sx={{
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            Email content is auto-saved locally. You can restore previous email contents below. (Not save draft)
                        </Typography>
                        <Box>
                            <Button
                                variant='outlined'
                                color='warning'
                                onClick={() => {setPrevModalOpen(true);}}
                                sx={{
                                    mb: 2,
                                }}
                            >
                                Previous Email Contents (only on this local device)
                            </Button>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <TextField
                            variant="outlined"
                            type="text"
                            label="Sender Email"
                            onChange={(e) => setSenderEmail(e.target.value)}
                            value={senderEmail}
                            sx={{
                                minWidth: 300,
                            }}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label="Author"
                            onChange={(e) => setAuthor(e.target.value)}
                            value={author}
                        />
                        <Box mt={-1}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DatePicker']}>
                                    <DatePicker
                                        label="Date"
                                        value={date}
                                        onChange={(newValue) => setDate(newValue)}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <TextField
                            variant="outlined"
                            type="text"
                            label="Recipient Email"
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            value={recipientEmail}
                            sx={{
                                minWidth: 300,
                            }}
                        />
                    </Box>
                    {autoReportLogLinked && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Sending this email will mark the office's 2-remaining auto-report reminder for this student as sent.">
                                <Chip
                                    label="Linked to 2-rem auto-report"
                                    color="info"
                                    size="small"
                                    variant="outlined"
                                />
                            </Tooltip>
                        </Box>
                    )}
                    <TextField
                        variant="outlined"
                        type="text"
                        label="Subject"
                        onChange={(e) => setTitle(e.target.value)}
                        value={title}
                        sx={{
                            maxWidth: 1000,
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            {semesters.map((semester, index) => {
                                return (
                                    <SemesterChipWithDialog
                                        semester={semester}
                                        handleChipClick={handleChipClick}
                                        season={season}
                                        selectedSemester={selectedSemester}
                                        key={index}
                                    />
                                )
                            })}
                        </Box>
                    </Box>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            marginBottom: 2,
                        }}
                    >
                        {categoryWithTemplates.map((course_type, i) => {
                            return (
                                <Box 
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginBottom: 2,
                                        border: `1px solid ${colors.grey[700]}`,
                                        borderRadius: 1,
                                        padding: 2,
                                    }}
                                >
                                    <Typography
                                        variant="h3"
                                        fontWeight={700}
                                        color={colors.primary[100]}
                                    >
                                        {course_type.course_type}
                                    </Typography>
                                    {course_type.categories.map((category, index) => {
                                        if (category.courses.length === 0) return null;

                                        return (
                                            <FormControl sx={{ mt: 2, marginX: 3 }} component="fieldset" variant="standard" key={index}>
                                                <FormLabel component="legend" sx={{ fontSize: "20px", color: colors.primary[100] }}>
                                                    {category.category} ({category.courses.length})
                                                </FormLabel>
                                                <FormGroup row={true}>
                                                    {category.courses.map((course, index) => {
                                                        return (
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox 
                                                                        checked={courseTitleCheckedMap[course.course_title + "|" + course.course_id] || false}
                                                                        onChange={(e) => handleChange(e, category.category, course)}
                                                                        name={course.course_title}
                                                                    />
                                                                }
                                                                label={course.course_title}
                                                                key={index}
                                                            />
                                                        )
                                                    })}
                                                </FormGroup>
                                            </FormControl>
                                        )
                                    })}
                                </Box>
                            )
                        })}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Editor
                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                            init={options}
                            value={emailContent}
                            onEditorChange={
                                (newValue, editor) => {
                                    setEmailContent(newValue);
                                }
                            }
                        />
                        {(autoSaveStatus === 'saved' || autoSaveStatus === 'error') && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    backgroundColor: autoSaveStatus === 'saved' ? colors.greenAccent[900] : colors.redAccent[900],
                                    border: `1px solid ${autoSaveStatus === 'saved' ? colors.greenAccent[700] : colors.redAccent[500]}`,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: autoSaveStatus === 'saved' ? colors.greenAccent[300] : colors.redAccent[300],
                                        fontWeight: 600,
                                    }}
                                >
                                    {autoSaveStatus === 'saved' ? '✓ Auto-saved locally' : '✕ Auto-save failed (storage full)'}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Student Info Card Generator */}
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-start"
                    >
                        <StudentInfoCardModal
                            student_uuid={student_uuid}
                            setErrorAlertMessage={setErrorAlertMessage}
                            setErrorAlertOpen={setErrorAlertOpen}
                            setSuccessAlertMessage={setSuccessAlertMessage}
                            setSuccessAlertOpen={setSuccessAlertOpen}
                        />
                    </Box>

                    {/* File Attach */}
                    <Box
                        display="flex"
                        minWidth="100%"
                        flexDirection="column"
                        justifyContent="flex-start"
                        sx={{
                            gap: "1rem",
                            ...minWidth90Rem && {
                                justifyContent: "space-between",
                                flexDirection: "row",
                            },
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                mb={-2}
                            >
                                <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                                    Attach Files
                                </Typography>
                                <IconButton component="label">
                                    <FileUploadOutlined />
                                    <input
                                        multiple={true}
                                        styles={{display:"none"}}
                                        type="file"
                                        hidden
                                        onChange={handleUpload}
                                        name="[licenseFile]"
                                    />
                                </IconButton>
                            </Box>
                            <Box
                                display="flex"
                                minWidth="100%"
                                flexDirection="column"
                            >
                                {files.length > 0 && files.map((file, index) => {
                                    return (
                                        <Box key={index} display="flex" alignItems="center">
                                            <Typography variant="body1" color={colors.primary[450]}>
                                                {file.name}
                                            </Typography>
                                            <IconButton onClick={(e) => handleFileRemove(e, index)}>
                                                <ClearIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                })}
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 4,
                        }}
                    >
                        <Typography 
                            variant="h3" 
                            fontWeight={700}
                            color={colors.primary[100]}
                        >
                            Email Preview
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                padding: 2,
                                borderRadius: 1,
                                border: `1px solid ${colors.primary[100]}`,
                                minHeight: 1000,
                            }}
                        >
                            <Typography variant="h5" color={colors.primary[100]}>From: <span style={{ padding: "6px 40px", backgroundColor: colors.primary[600] }}>{senderEmail}</span></Typography>
                            <Typography variant="h5" color={colors.primary[100]}>Author: <span style={{ padding: "6px 40px", backgroundColor: colors.primary[600] }}>{author}</span></Typography>
                            <Typography variant="h5" color={colors.primary[100]}>Date: <span style={{ padding: "6px 40px", backgroundColor: colors.primary[600] }}>{date.format('YYYY-MM-DD')}</span></Typography>
                            <Typography variant="h5" color={colors.primary[100]}>Subject: <span style={{ padding: "6px 40px", backgroundColor: colors.primary[600] }}>{title}</span></Typography>
                            <div 
                                dangerouslySetInnerHTML={{ __html: pToDiv(emailContent) + endOfEmail }} 
                                style={{
                                    minHeight: 800,
                                    padding: "10px 20px",
                                    backgroundColor: colors.primary[600],
                                }}
                            />
                            <Typography variant="h5" color={colors.primary[100]}>Attached Files{files.length > 0 ? `(${files.length})` : null}: </Typography>
                            {files.length > 0 && files.map((file, index) => {
                                return (
                                    <Typography variant="h5" color={colors.primary[100]} key={index}><span style={{ padding: "6px 40px", backgroundColor: colors.primary[600] }}>{file.name}</span></Typography>
                                )
                            })}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enableFollowup}
                                    onChange={(e) => setEnableFollowup(e.target.checked)}
                                    color="success"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Auto follow-up (3 days)
                                    </Typography>
                                    {enableFollowup && (
                                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                            3일 내 답장이 없으면 자동으로 후속 이메일을 보냅니다
                                        </Typography>
                                    )}
                                </Box>
                            }
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            mt: 4,
                            mb: 20,
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: colors.blueAccent[600],
                                color: colors.primary[450],
                                width: 200,
                                minHeight: 50,
                                '&:hover': {
                                    backgroundColor: colors.blueAccent[300],
                                },
                            }}
                            onClick={handleSaveDraft}
                        >
                            {draftId === null ? "Save Draft" : "Update Draft"}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: colors.greenAccent[500],
                                color: colors.primary[450],
                                width: 200,
                                minHeight: 50,
                                '&:hover': {
                                    backgroundColor: colors.greenAccent[400],
                                },
                            }}
                            onClick={handleSendEmail}
                            disabled={gmailFailedDialogOpen || backdropOpen || gmailAuthRequiredOpen || gmailAuthChecking}
                        >
                            Send Email
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: colors.blueAccent[600],
                                color: colors.primary[450],
                                width: 200,
                                minHeight: 50,
                                '&:hover': {
                                    backgroundColor: colors.blueAccent[500],
                                },
                            }}
                            onClick={() => setUpdatePortalModalOpen(true)}
                        >
                            Update to Parent Portal
                        </Button>
                    </Box>
                    <BackdropComp backdropOpen={backdropOpen} handleBackdropClose={() => setBackdropOpen(false)} />
                </Box>

                {/* Draggable separator + Communication Context Panel (right side) */}
                {student?.id && isWideScreen && (
                    <>
                    {/* Draggable separator */}
                    <Box
                        onMouseDown={handleSeparatorMouseDown}
                        sx={{
                            width: 6, cursor: 'col-resize', flexShrink: 0,
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: colors.primary[600] },
                            transition: 'background-color 0.2s',
                            position: 'sticky', top: 16, alignSelf: 'flex-start',
                            height: 'calc(100vh - 100px)',
                        }}
                    />
                    {/* Context Panel — sticky so it stays visible while scrolling */}
                    <Paper sx={{
                        width: `${contextWidthPercent}%`, flexShrink: 0, display: 'flex', flexDirection: 'column',
                        position: 'sticky', top: 16, alignSelf: 'flex-start',
                        maxHeight: 'calc(100vh - 100px)', overflow: 'hidden',
                    }}>
                        <Box sx={{ p: 1.5, backgroundColor: colors.primary[600], flexShrink: 0 }}>
                            <Typography variant="h6" fontWeight="bold">Communication Context</Typography>
                        </Box>
                        {/* Student Info — pinned */}
                        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h5" fontWeight="bold">
                                    {`(${student.email_code ?? 'N/A'}) ${student.consultation_number ?? 'N/A'}`}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">
                                    {student.parent_name || ''}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Email:</strong> {student.parent_email || 'N/A'}</Typography>
                                <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Phone:</strong> {student.parent_phone || 'N/A'}</Typography>
                                {student.consultation_date && <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Consult:</strong> {student.consultation_date}</Typography>}
                                {student.info_mail?.date && <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Sent:</strong> {new Date(student.info_mail.date).toLocaleDateString()}</Typography>}
                            </Box>
                        </Box>
                        {/* Top half: Summary (scrollable) */}
                        <Box sx={{ height: 0, flex: 1, maxHeight: 'calc(50vh - 50px)', overflow: 'auto', overscrollBehavior: 'contain', borderBottom: 1, borderColor: 'divider' }}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                    {summaryLoading ? (
                                        <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
                                    ) : (
                                        <Box>
                                            {summaryData?.summary ? (
                                                <SummaryWithRefs summary={summaryData.summary} onRefClick={handleRefClick} />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No summary available yet. Click refresh to generate.
                                                </Typography>
                                            )}
                                            <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                                                {summaryData?.generated_at && (
                                                    <Chip
                                                        label={`Updated: ${new Date(summaryData.generated_at).toLocaleDateString()}`}
                                                        size="small" variant="outlined"
                                                    />
                                                )}
                                                {summaryData?.is_stale && (
                                                    <Chip label="New data available" size="small" color="warning" />
                                                )}
                                                <Tooltip title="Refresh summary">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => refreshSummary(student.id)}
                                                            disabled={summaryRefreshing}
                                                        >
                                                            {summaryRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {gmailAuthorized === false && (
                                                    <Chip
                                                        label="Connect Gmail"
                                                        size="small" color="warning" variant="outlined"
                                                        onClick={handleConnectGmail}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                        {/* Bottom half: Emails / Memos Tabs */}
                        <Box sx={{ height: 0, flex: 1, maxHeight: 'calc(50vh - 50px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                                    <Tabs value={contextTab} onChange={handleContextTabChange} variant="fullWidth">
                                        <Tab label={`Emails${summaryData?.source_email_count ? ` (${summaryData.source_email_count})` : ''}`} />
                                        <Tab label={`Memos${summaryData?.source_memo_count ? ` (${summaryData.source_memo_count})` : ''}`} />
                                    </Tabs>
                                </Box>
                                <Box ref={contextScrollRef} sx={{ p: 2, flex: 1, overflow: 'auto', overscrollBehavior: 'contain' }}>
                            {/* Emails Tab */}
                            {contextTab === 0 && (
                                (rawEmailsLoading || gmailAuthorized === null) ? (
                                    <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
                                ) : !gmailAuthorized ? (
                                    <Box textAlign="center" py={2}>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Gmail is not connected.
                                        </Typography>
                                        <Button variant="outlined" size="small" onClick={handleConnectGmail}>
                                            Connect Gmail
                                        </Button>
                                    </Box>
                                ) : rawEmails.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                        No email history found.
                                    </Typography>
                                ) : (
                                    rawEmails.map((email, idx) => (
                                        <Accordion key={email.messageId || idx} id={`ref-email-${idx}`} disableGutters
                                            expanded={expandedEmailIdx === idx}
                                            onChange={(_, isExpanded) => setExpandedEmailIdx(isExpanded ? idx : null)}
                                            sx={{
                                                transition: 'background-color 0.5s, border-color 0.5s',
                                                ...(highlightedRef?.type === 'email' && highlightedRef?.index === idx && {
                                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                    border: '1px solid',
                                                    borderColor: 'primary.main',
                                                }),
                                            }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Chip
                                                            label={isSentEmail(email) ? 'Sent' : 'Received'}
                                                            size="small"
                                                            color={isSentEmail(email) ? 'info' : 'success'}
                                                            sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                                                        />
                                                        <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, wordBreak: 'break-word' }}>
                                                            {email.subject}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {email.from?.email}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(email.date).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                                                    {highlightedRef?.type === 'email' && highlightedRef?.index === idx && highlightedRef?.quote
                                                        ? <HighlightedText text={email.body || email.snippet || '(No content)'} quote={highlightedRef.quote} />
                                                        : (email.body || email.snippet || '(No content)')}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))
                                )
                            )}

                            {/* Memos Tab */}
                            {contextTab === 1 && (
                                rawMemosLoading ? (
                                    <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
                                ) : rawMemos.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                        No memos for this student.
                                    </Typography>
                                ) : (
                                    rawMemos.map((memo, idx) => (
                                        <Paper key={memo.id} id={`ref-memo-${idx}`} variant="outlined" sx={{
                                            p: 1.5, mb: 1,
                                            transition: 'background-color 0.5s, border-color 0.5s',
                                            ...(highlightedRef?.type === 'memo' && highlightedRef?.index === idx && {
                                                backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
                                                border: '1px solid',
                                                borderColor: 'warning.main',
                                            }),
                                        }}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                {memo.bookmark && <BookmarkIcon fontSize="small" color="warning" />}
                                                {memo.tasks?.length > 0 && (
                                                    <CancelScheduleSendIcon
                                                        fontSize="small"
                                                        color={memo.tasks[0].is_info_mail ? "error" : "warning"}
                                                    />
                                                )}
                                                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                                    {memo.title || '(Untitled)'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
                                                    {new Date(memo.createdAt).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                            {memo.content && (
                                                highlightedRef?.type === 'memo' && highlightedRef?.index === idx && highlightedRef?.quote ? (
                                                    <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                        <HighlightedText text={stripHtml(memo.content)} quote={highlightedRef.quote} />
                                                    </Typography>
                                                ) : (
                                                    <MemoContent
                                                        component={Typography}
                                                        variant="body2"
                                                        color="text.secondary"
                                                        html={memo.content}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                )
                                            )}
                                        </Paper>
                                    ))
                                )
                                )}
                                </Box>
                            </Box>
                    </Paper>
                    </>
                )}

                {/* Floating context panel for narrow screens — moved outside container below */}

                </Box>
            }

            {/* Floating Communication Context for narrow screens */}
            {student?.id && !isWideScreen && (
                <Box sx={{
                    position: 'fixed',
                    top: '50%',
                    right: contextOpen ? 40 : -380,
                    transform: 'translateY(-50%)',
                    zIndex: 100,
                    transition: 'right 0.5s',
                    width: 420,
                    height: '70%',
                }}>
                    <IconButton
                        onClick={() => setContextOpen(!contextOpen)}
                        sx={{
                            position: 'absolute',
                            left: -36,
                            top: 2,
                            backgroundColor: colors.grey[800],
                            color: '#fff',
                            borderRadius: '8px 0 0 8px',
                            width: 36,
                            height: 80,
                            zIndex: 101,
                            boxShadow: 4,
                            '&:hover': { backgroundColor: colors.grey[700] },
                        }}
                    >
                        {contextOpen ?
                            <KeyboardDoubleArrowRightIcon /> :
                            <KeyboardDoubleArrowLeftIcon />}
                    </IconButton>
                <Paper sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: 10,
                    border: `5px solid ${colors.grey[800]}`,
                }}>
                    <Box sx={{ p: 1.5, backgroundColor: colors.primary[600] }}>
                        <Typography variant="h6" fontWeight="bold">Communication Context</Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {/* Compact Student Info */}
                            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="h5" fontWeight="bold">
                                        {`(${student.email_code ?? 'N/A'}) ${student.consultation_number ?? 'N/A'}`}
                                    </Typography>
                                    <Typography variant="h6" color="text.secondary">
                                        {student.parent_name || ''}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Email:</strong> {student.parent_email || 'N/A'}</Typography>
                                    <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Phone:</strong> {student.parent_phone || 'N/A'}</Typography>
                                    {student.consultation_date && <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Consult:</strong> {student.consultation_date}</Typography>}
                                    {student.info_mail?.date && <Typography variant="body1" sx={{ fontSize: 15 }}><strong>Sent:</strong> {new Date(student.info_mail.date).toLocaleDateString()}</Typography>}
                                </Box>
                            </Box>
                            {/* Summary — always visible */}
                            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                                {summaryLoading ? <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box> : (
                                    <Box>
                                        {summaryData?.summary ? (
                                            <SummaryWithRefs summary={summaryData.summary} onRefClick={handleRefClick} />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No summary available yet.</Typography>
                                        )}
                                        <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                                            {summaryData?.generated_at && <Chip label={`Updated: ${new Date(summaryData.generated_at).toLocaleDateString()}`} size="small" variant="outlined" />}
                                            {summaryData?.is_stale && <Chip label="New data available" size="small" color="warning" />}
                                            <Tooltip title="Refresh summary"><span>
                                                <IconButton size="small" onClick={() => refreshSummary(student.id)} disabled={summaryRefreshing}>
                                                    {summaryRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                                                </IconButton>
                                            </span></Tooltip>
                                            {gmailAuthorized === false && <Chip label="Connect Gmail" size="small" color="warning" variant="outlined" onClick={handleConnectGmail} sx={{ cursor: 'pointer' }} />}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={contextTab} onChange={handleContextTabChange}>
                                    <Tab label={`Emails${summaryData?.source_email_count ? ` (${summaryData.source_email_count})` : ''}`} />
                                    <Tab label={`Memos${summaryData?.source_memo_count ? ` (${summaryData.source_memo_count})` : ''}`} />
                                </Tabs>
                            </Box>
                            <Box ref={contextScrollRef} sx={{ p: 2, maxHeight: 400, overflow: 'auto', overscrollBehavior: 'contain' }}>
                                {contextTab === 0 && (
                                    (rawEmailsLoading || gmailAuthorized === null) ? <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box> :
                                    !gmailAuthorized ? <Box textAlign="center" py={2}><Typography variant="body2" color="text.secondary" mb={1}>Gmail not connected.</Typography><Button variant="outlined" size="small" onClick={handleConnectGmail}>Connect Gmail</Button></Box> :
                                    rawEmails.length === 0 ? <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No email history found.</Typography> :
                                    rawEmails.map((email, idx) => (
                                        <Accordion key={email.messageId || idx} id={`ref-email-${idx}`} disableGutters
                                            expanded={expandedEmailIdx === idx}
                                            onChange={(_, isExpanded) => setExpandedEmailIdx(isExpanded ? idx : null)}
                                            sx={{
                                                transition: 'background-color 0.5s, border-color 0.5s',
                                                ...(highlightedRef?.type === 'email' && highlightedRef?.index === idx && {
                                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                    border: '1px solid',
                                                    borderColor: 'primary.main',
                                                }),
                                            }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Chip
                                                            label={isSentEmail(email) ? 'Sent' : 'Received'}
                                                            size="small"
                                                            color={isSentEmail(email) ? 'info' : 'success'}
                                                            sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                                                        />
                                                        <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>{email.subject}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="text.secondary">{email.from?.email}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{new Date(email.date).toLocaleDateString()}</Typography>
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {highlightedRef?.type === 'email' && highlightedRef?.index === idx && highlightedRef?.quote
                                                        ? <HighlightedText text={email.body || email.snippet || '(No content)'} quote={highlightedRef.quote} />
                                                        : (email.body || email.snippet || '(No content)')}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))
                                )}
                                {contextTab === 1 && (
                                    rawMemosLoading ? <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box> :
                                    rawMemos.length === 0 ? <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No memos.</Typography> :
                                    rawMemos.map((memo, idx) => (
                                        <Paper key={memo.id} id={`ref-memo-${idx}`} variant="outlined" sx={{
                                            p: 1.5, mb: 1,
                                            transition: 'background-color 0.5s, border-color 0.5s',
                                            ...(highlightedRef?.type === 'memo' && highlightedRef?.index === idx && {
                                                backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
                                                border: '1px solid',
                                                borderColor: 'warning.main',
                                            }),
                                        }}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                {memo.bookmark && <BookmarkIcon fontSize="small" color="warning" />}
                                                {memo.tasks?.length > 0 && (
                                                    <CancelScheduleSendIcon
                                                        fontSize="small"
                                                        color={memo.tasks[0].is_info_mail ? "error" : "warning"}
                                                    />
                                                )}
                                                <Typography variant="subtitle2" sx={{ flex: 1 }}>{memo.title || '(Untitled)'}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>{new Date(memo.createdAt).toLocaleDateString()}</Typography>
                                            </Box>
                                            {memo.content && (
                                                highlightedRef?.type === 'memo' && highlightedRef?.index === idx && highlightedRef?.quote ? (
                                                    <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                        <HighlightedText text={stripHtml(memo.content)} quote={highlightedRef.quote} />
                                                    </Typography>
                                                ) : (
                                                    <MemoContent
                                                        component={Typography}
                                                        variant="body2"
                                                        color="text.secondary"
                                                        html={memo.content}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                )
                                            )}
                                        </Paper>
                                    ))
                                )}
                            </Box>
                    </Box>
                </Paper>
                </Box>
            )}
            {/* Floating student card — hidden when student context panel is available */}
            {!student?.id && (
                <>
                <Box
                    sx={{
                        position: 'fixed',
                        top: '13%',
                        right: !consultationCardToggle ? 30 : -300,
                        transform: 'translateY(-50%)',
                        zIndex: 101,
                        transition: 'right 0.5s',
                        minWidth: 400,
                        maxWidth: 500,
                    }}
                >
                    <IconButton
                        onClick={() => setConsultationCardToggle(!consultationCardToggle)}
                    >
                        {consultationCardToggle ?
                        <h4>Show</h4> : <h4>Hide</h4>
                        }
                        {consultationCardToggle ?
                        <KeyboardDoubleArrowLeftIcon fontSize='large' /> :
                        <KeyboardDoubleArrowRightIcon fontSize='large' />}
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        right: !consultationCardToggle ? 40 : -300,
                        transform: 'translateY(-50%)',
                        backgroundColor: colors.primary[600],
                        border: `5px solid ${colors.grey[800]}`,
                        boxShadow: 10,
                        zIndex: 100,
                        padding: 2,
                        transition: 'right 0.5s',
                        height: "70%",
                        width: 400,
                    }}
                >
                    <ConsultationCardReadOnly student={student} />
                </Box>
                </>
            )}
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical='bottom'
                horizontal='left'
            />
            <Dialog
                open={gmailFailedDialogOpen}
                onClose={() => setGmailFailedDialogOpen(false)}
            >
                <DialogTitle>Gmail API 전송 실패</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Gmail API를 통한 이메일 전송에 실패했습니다. 일반 SMTP로 전송할 수 있지만, 자동 후속 이메일 기능은 사용할 수 없습니다.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGmailFailedDialogOpen(false)}>
                        취소
                    </Button>
                    <Button onClick={handleSmtpFallback} variant="contained" color="primary">
                        SMTP로 전송
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={gmailAuthRequiredOpen}
                onClose={() => { /* require explicit choice */ }}
                disableEscapeKeyDown
            >
                <DialogTitle>Gmail is not connected</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <p>
                            Gmail must be authorized before sending. Authorizing also enables
                            auto follow-up tracking. You can also send via SMTP, but the auto
                            follow-up feature will <strong>not</strong> be available.
                        </p>
                        {gmailAuthError && (
                            <p style={{ color: '#d32f2f' }}>{gmailAuthError}</p>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAuthRequiredCancel} disabled={gmailAuthChecking}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAuthRequiredSmtpFallback}
                        disabled={gmailAuthChecking}
                    >
                        Send via SMTP
                    </Button>
                    {!gmailAuthorizeStarted ? (
                        <Button
                            onClick={handleAuthorizeGmail}
                            variant="contained"
                            color="primary"
                            disabled={gmailAuthChecking}
                        >
                            {gmailAuthChecking ? "Opening..." : "Authorize Gmail"}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleAuthRecheckAndSend}
                            variant="contained"
                            color="primary"
                            disabled={gmailAuthChecking}
                        >
                            {gmailAuthChecking ? "Checking..." : "I've authorized — Retry"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog
                open={followupRetryOpen}
                onClose={() => { /* require explicit choice */ }}
                disableEscapeKeyDown
            >
                <DialogTitle>Follow-up tracking could not be enabled</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <p>
                            The email was sent successfully, but saving the follow-up
                            tracking record failed. The auto follow-up will <strong>not</strong> run
                            unless you retry.
                        </p>
                        {followupRetryPayload && (
                            <Box sx={{ mt: 1, mb: 1, fontSize: '0.9em' }}>
                                <div><strong>To:</strong> {followupRetryPayload.recipient_email}</div>
                                <div><strong>Subject:</strong> {followupRetryPayload.subject}</div>
                            </Box>
                        )}
                        {followupRetryError && (
                            <p style={{ color: '#d32f2f' }}>{followupRetryError}</p>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setFollowupRetryOpen(false);
                            setFollowupRetryPayload(null);
                            setFollowupRetryError("");
                        }}
                        disabled={followupRetryLoading}
                    >
                        Skip
                    </Button>
                    <Button
                        onClick={handleFollowupRetry}
                        variant="contained"
                        color="primary"
                        disabled={followupRetryLoading}
                    >
                        {followupRetryLoading ? "Retrying..." : "Retry"}
                    </Button>
                </DialogActions>
            </Dialog>
            <PrevEmailContentModal
                setModalOpen={setPrevModalOpen}
                modalOpen={prevModalOpen}
                pToDiv={pToDiv}
                endOfEmail={endOfEmail}
                setEmailContent={setEmailContent}
                setDraft={setDraft}
                setTitle={setTitle}
                setSenderEmail={setSenderEmail}
                setRecipientEmail={setRecipientEmail}
                setAuthor={setAuthor}
                setDate={setDate}
            />
            <UpdatePortalModal
                modalOpen={updatePortalModalOpen}
                setModalOpen={setUpdatePortalModalOpen}
                studentUuid={student_uuid}
                selectedCourses={selectedCourses}
                courseTitleCheckedMap={courseTitleCheckedMap}
                emailContent={emailContent}
                author={author}
                selectedSemester={selectedSemester}
                onSuccess={(msg) => {
                    setSuccessAlertMessage(msg);
                    setSuccessAlertOpen(true);
                }}
                onError={(msg) => {
                    setErrorAlertMessage(msg);
                    setErrorAlertOpen(true);
                }}
                handleLogout={handleLogout}
            />
            <Dialog
                open={restoreDialogOpen}
                onClose={handleSkipRestore}
            >
                <DialogTitle>Restore Previous Email</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Found a previously saved email for this student
                        {restoreDataRef.current?.savedAt && (
                            <> from <strong>{new Date(restoreDataRef.current.savedAt).toLocaleString()}</strong></>
                        )}
                        . Would you like to restore it?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipRestore}>No, start fresh</Button>
                    <Button onClick={handleRestore} variant="contained" color="primary">Restore</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default EmailGenerator;